import { useEffect, useRef } from "react";
import { BEFORE_RELOAD_EVENT } from "@/contexts/UpdateContext";

/**
 * Persiste o estado em edição no localStorage pra que:
 *  - Se um reload forçado por update novo acontecer, o usuário não perca o
 *    trabalho — o flush é feito sincronamente ao receber `app:before-update-reload`.
 *  - Em qualquer outro reload (refresh manual, crash), o rascunho ainda
 *    está lá e pode ser oferecido pra restauração via useDraftRestore.
 *
 * Características:
 *  - Debounce de 1s pra escritas normais (não martela o localStorage).
 *  - Flush imediato no evento de pré-reload.
 *  - Só grava quando `enabled=true` (tipicamente: form sujo).
 *  - TTL de 7 dias na leitura — drafts velhos são ignorados.
 *
 * Uso típico:
 *   const { isDirty } = useFormState();
 *   useDraftAutosave({ key: `flow:${id}`, value: state, enabled: isDirty });
 */

const STORAGE_PREFIX = "draft:";
const DEBOUNCE_MS = 1000;
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

type Envelope<T> = { value: T; savedAt: number; version: number };
const ENVELOPE_VERSION = 1;

function storageKey(key: string) {
  return `${STORAGE_PREFIX}${key}`;
}

function writeDraft<T>(key: string, value: T) {
  try {
    const envelope: Envelope<T> = { value, savedAt: Date.now(), version: ENVELOPE_VERSION };
    localStorage.setItem(storageKey(key), JSON.stringify(envelope));
  } catch {
    // Quota cheia, modo privado, etc. — falha silenciosa por design.
  }
}

export function readDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const env = JSON.parse(raw) as Envelope<T>;
    if (env.version !== ENVELOPE_VERSION) return null;
    if (Date.now() - env.savedAt > TTL_MS) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    return env.value;
  } catch {
    return null;
  }
}

export function clearDraft(key: string) {
  try { localStorage.removeItem(storageKey(key)); } catch { /* noop */ }
}

export interface UseDraftAutosaveOptions<T> {
  key: string;
  value: T;
  /** Quando false, não grava (mas mantém o último valor pra flush em pré-reload). */
  enabled?: boolean;
}

export function useDraftAutosave<T>({ key, value, enabled = true }: UseDraftAutosaveOptions<T>) {
  // Mantemos uma ref com o valor atual pra o listener de pré-reload poder
  // ler sincronamente sem depender do closure do useEffect.
  const valueRef = useRef(value);
  valueRef.current = value;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const keyRef = useRef(key);
  keyRef.current = key;

  // Debounced write em mudanças normais.
  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => writeDraft(key, value), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [key, value, enabled]);

  // Flush síncrono em pré-reload — não importa se está enabled, gravamos
  // sempre que houver algo (o usuário pode ter pausado a edição mas o
  // último estado ainda é valioso).
  useEffect(() => {
    const flush = () => {
      if (!enabledRef.current) return;
      writeDraft(keyRef.current, valueRef.current);
    };
    window.addEventListener(BEFORE_RELOAD_EVENT, flush);
    return () => window.removeEventListener(BEFORE_RELOAD_EVENT, flush);
  }, []);
}
